/**
 * Composable for Google Fonts integration
 * Handles font loading, injection, and state management
 */

import { ref, computed, Ref, ComputedRef } from 'vue';
import googleFontsService, {
  GoogleFont,
  FONT_CATEGORIES,
  EMAIL_SAFE_FONTS,
  FontCategory,
} from './GoogleFontsService';

export interface UseGoogleFontsOptions {
  preloadPopular?: boolean;
  maxPreload?: number;
}

export interface UseGoogleFontsReturn {
  // State
  fonts: Ref<GoogleFont[]>;
  isLoading: Ref<boolean>;
  error: Ref<string | null>;
  searchQuery: Ref<string>;
  selectedCategory: Ref<string>;
  favorites: Ref<string[]>;
  recentFonts: Ref<string[]>;

  // Computed
  filteredFonts: ComputedRef<GoogleFont[]>;
  favoriteFonts: ComputedRef<GoogleFont[]>;
  recentFontsList: ComputedRef<GoogleFont[]>;
  emailSafeFonts: ComputedRef<typeof EMAIL_SAFE_FONTS>;
  categories: ComputedRef<FontCategory[]>;

  // Methods
  loadFonts: () => Promise<void>;
  searchFonts: (query: string) => void;
  filterByCategory: (category: string) => void;
  addToFavorites: (fontFamily: string) => void;
  removeFromFavorites: (fontFamily: string) => void;
  isFavorite: (fontFamily: string) => boolean;
  addToRecent: (fontFamily: string) => void;
  injectFontToIframe: (fontFamily: string, iframeDocument?: Document) => void;
  removeFontFromIframe: (fontFamily: string, iframeDocument?: Document) => void;
  generateFontValue: (fontFamily: string, isEmailSafe?: boolean) => string;
  isEmailSafe: (fontFamily: string) => boolean;
  getEmailFallback: (fontFamily: string) => string;
  extractGoogleFontsFromDocument: (doc: Document) => string[];
  scanAndInjectGoogleFonts: (iframeDocument: Document) => string[];
  getDetectedTemplateFonts: () => Array<{ label: string; value: string; isGoogleFont: boolean }>;
  clearDetectedTemplateFonts: () => void;
}

// Track loaded fonts to avoid duplicate injections
const loadedFonts = new Set<string>();

// Global state for Google Fonts detected in current template (shared across components)
const detectedTemplateFonts = ref<Array<{ label: string; value: string; isGoogleFont: boolean }>>([]);

export function useGoogleFonts(options: UseGoogleFontsOptions = {}): UseGoogleFontsReturn {
  const fonts = ref<GoogleFont[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const searchQuery = ref('');
  const selectedCategory = ref('all');
  const favorites = ref<string[]>([]);
  const recentFonts = ref<string[]>([]);

  // Load fonts from service
  const loadFonts = async (): Promise<void> => {
    if (isLoading.value) return;

    isLoading.value = true;
    error.value = null;

    try {
      fonts.value = await googleFontsService.fetchFonts();
      favorites.value = googleFontsService.getFavorites();
      recentFonts.value = googleFontsService.getRecentFonts();

      // Preload popular fonts if requested
      if (options.preloadPopular) {
        const toPreload = fonts.value.slice(0, options.maxPreload || 20);
        toPreload.forEach((font) => preloadFont(font.family));
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to load fonts';
      console.error('[useGoogleFonts] Error loading fonts:', e);
    } finally {
      isLoading.value = false;
    }
  };

  // Preload font CSS without injecting
  const preloadFont = (fontFamily: string): void => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'style';
    link.href = googleFontsService.generateFontLink(fontFamily);
    document.head.appendChild(link);
  };

  // Filtered fonts based on search and category
  const filteredFonts = computed(() => {
    let result = fonts.value;

    // Filter by category
    if (selectedCategory.value !== 'all') {
      result = result.filter((font) => font.category === selectedCategory.value);
    }

    // Filter by search query
    if (searchQuery.value.trim()) {
      const query = searchQuery.value.toLowerCase();
      result = result.filter((font) => font.family.toLowerCase().includes(query));
    }

    return result;
  });

  // Favorite fonts
  const favoriteFonts = computed(() => fonts.value.filter((font) => favorites.value.includes(font.family)));

  // Recent fonts
  const recentFontsList = computed(() => {
    const recentFamilies = recentFonts.value;
    return fonts.value
      .filter((font) => recentFamilies.includes(font.family))
      .sort((a, b) => recentFamilies.indexOf(a.family) - recentFamilies.indexOf(b.family));
  });

  // Email-safe fonts
  const emailSafeFonts = computed(() => EMAIL_SAFE_FONTS);

  // Categories
  const categories = computed(() => FONT_CATEGORIES);

  // Search fonts
  const searchFonts = (query: string): void => {
    searchQuery.value = query;
  };

  // Filter by category
  const filterByCategory = (category: string): void => {
    selectedCategory.value = category;
  };

  // Add to favorites
  const addToFavorites = (fontFamily: string): void => {
    googleFontsService.addFavorite(fontFamily);
    favorites.value = googleFontsService.getFavorites();
  };

  // Remove from favorites
  const removeFromFavorites = (fontFamily: string): void => {
    googleFontsService.removeFavorite(fontFamily);
    favorites.value = googleFontsService.getFavorites();
  };

  // Check if favorite
  const isFavorite = (fontFamily: string): boolean => favorites.value.includes(fontFamily);

  // Add to recent
  const addToRecent = (fontFamily: string): void => {
    googleFontsService.addRecentFont(fontFamily);
    recentFonts.value = googleFontsService.getRecentFonts();
  };

  // Inject font CSS into iframe
  const injectFontToIframe = (fontFamily: string, iframeDocument?: Document): void => {
    const doc = iframeDocument || document;
    const fontId = `google-font-${fontFamily.replace(/\s+/g, '-').toLowerCase()}`;

    // Skip if already loaded
    if (loadedFonts.has(fontFamily) && doc.getElementById(fontId)) {
      return;
    }

    // Skip email-safe fonts (already available)
    if (googleFontsService.isEmailSafe(fontFamily)) {
      return;
    }

    // Create and inject link element
    const link = doc.createElement('link');
    link.id = fontId;
    link.rel = 'stylesheet';
    link.href = googleFontsService.generateFontLink(fontFamily);

    // For iframe, inject into head
    const head = doc.head || doc.getElementsByTagName('head')[0];
    if (head) {
      head.appendChild(link);
      loadedFonts.add(fontFamily);
    }
  };

  // Remove font from iframe
  const removeFontFromIframe = (fontFamily: string, iframeDocument?: Document): void => {
    const doc = iframeDocument || document;
    const fontId = `google-font-${fontFamily.replace(/\s+/g, '-').toLowerCase()}`;
    const existing = doc.getElementById(fontId);

    if (existing) {
      existing.remove();
      loadedFonts.delete(fontFamily);
    }
  };

  // Generate CSS font-family value
  const generateFontValue = (fontFamily: string, isEmailSafeFont?: boolean): string => {
    if (isEmailSafeFont || googleFontsService.isEmailSafe(fontFamily)) {
      const safeFontData = googleFontsService.getEmailSafeFontData(fontFamily);
      if (safeFontData) {
        return `"${safeFontData.family}", ${safeFontData.fallback}`;
      }
    }

    // Google Font with fallback
    const font = fonts.value.find((f) => f.family === fontFamily);
    const category = font?.category || 'sans-serif';
    return `"${fontFamily}", ${category}`;
  };

  // Check if email safe
  const isEmailSafe = (fontFamily: string): boolean => googleFontsService.isEmailSafe(fontFamily);

  // Get email fallback
  const getEmailFallback = (fontFamily: string): string => {
    const safeFontData = googleFontsService.getEmailSafeFontData(fontFamily);
    if (safeFontData) {
      return `"${safeFontData.family}", ${safeFontData.fallback}`;
    }

    // For Google Fonts, return a safe fallback based on category
    const font = fonts.value.find((f) => f.family === fontFamily);
    switch (font?.category) {
      case 'serif':
        return '"Georgia", serif';
      case 'monospace':
        return '"Courier New", monospace';
      case 'handwriting':
        return '"Comic Sans MS", cursive';
      default:
        return '"Arial", sans-serif';
    }
  };

  // Extract Google Fonts from a document
  const extractGoogleFontsFromDocument = (doc: Document): string[] => googleFontsService.extractGoogleFontsFromDocument(doc);

  // Scan document and inject all Google Fonts found, and store for FontFamily dropdowns
  const scanAndInjectGoogleFonts = (iframeDocument: Document): string[] => {
    const googleFonts = googleFontsService.extractGoogleFontsFromDocument(iframeDocument);

    // Inject each font into the iframe
    googleFonts.forEach((fontFamily) => {
      injectFontToIframe(fontFamily, iframeDocument);
    });

    // Store as dropdown options for FontFamily components
    detectedTemplateFonts.value = googleFonts.map((fontFamily) => {
      const font = fonts.value.find((f) => f.family === fontFamily);
      const category = font?.category || 'sans-serif';
      return {
        label: fontFamily,
        value: `"${fontFamily}", ${category}`,
        isGoogleFont: true,
      };
    });

    return googleFonts;
  };

  // Get detected Google Fonts for dropdown (already scanned)
  const getDetectedTemplateFonts = (): Array<{ label: string; value: string; isGoogleFont: boolean }> => {
    return detectedTemplateFonts.value;
  };

  // Clear detected fonts (call when template changes)
  const clearDetectedTemplateFonts = (): void => {
    detectedTemplateFonts.value = [];
  };

  return {
    // State
    fonts,
    isLoading,
    error,
    searchQuery,
    selectedCategory,
    favorites,
    recentFonts,

    // Computed
    filteredFonts,
    favoriteFonts,
    recentFontsList,
    emailSafeFonts,
    categories,

    // Methods
    loadFonts,
    searchFonts,
    filterByCategory,
    addToFavorites,
    removeFromFavorites,
    isFavorite,
    addToRecent,
    injectFontToIframe,
    removeFontFromIframe,
    generateFontValue,
    isEmailSafe,
    getEmailFallback,
    extractGoogleFontsFromDocument,
    scanAndInjectGoogleFonts,
    getDetectedTemplateFonts,
    clearDetectedTemplateFonts,
  };
}

export default useGoogleFonts;
